import { useState } from "react";
import { Save, Loader2, Check, Brain } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { TYPE_LABEL_NL } from "./SignalCard";
import { useT } from "../../lib/i18n";

const WEEKDAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

export default function AssistantSettingsPanel({ settings, profile, onSaved }) {
  const t = useT();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setOk(false); };
  const setTypeFreq = (t, v) => set("type_frequency", { ...(form.type_frequency || {}), [t]: v });

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.put("/assistant/settings", {
        enabled: form.enabled, frequency: form.frequency, run_time: form.run_time, weekly_day: Number(form.weekly_day),
        timezone: form.timezone, max_signals: Number(form.max_signals), type_frequency: form.type_frequency || {},
        tone_override: form.tone_override || null,
      });
      setForm(data); setOk(true); onSaved(data);
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const types = Object.keys(profile?.types || {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="assistant-settings-panel">
      <div className="pm-card p-5 space-y-4">
        <div className="font-display font-black text-sm">{t("Ritme")}</div>
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">{t("Assistent actief")}</div>
          <button type="button" onClick={() => set("enabled", !form.enabled)} data-testid="assistant-set-enabled"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${form.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-bold text-slate-600">{t("Frequentie")}
            <select className="pm-input mt-1 w-full" value={form.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="assistant-set-frequency">
              <option value="daily">{t("Dagelijks")}</option><option value="weekly">{t("Wekelijks")}</option>
            </select>
          </label>
          <label className="block text-xs font-bold text-slate-600">{t("Tijdstip")}
            <input type="time" className="pm-input mt-1 w-full" value={form.run_time} onChange={(e) => set("run_time", e.target.value)} data-testid="assistant-set-time" />
          </label>
        </div>
        {form.frequency === "weekly" && (
          <div className="flex gap-1" data-testid="assistant-set-weekday">
            {WEEKDAYS.map((d, i) => (
              <button key={d} onClick={() => set("weekly_day", i)} className={`h-8 w-9 rounded-lg text-xs font-bold border ${form.weekly_day === i ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>{t(d)}</button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-bold text-slate-600">{t("Max. signalen per cyclus")}
            <input type="number" min="3" max="15" className="pm-input mt-1 w-full" value={form.max_signals} onChange={(e) => set("max_signals", e.target.value)} data-testid="assistant-set-max" />
          </label>
          <label className="block text-xs font-bold text-slate-600">{t("Toon")}
            <select className="pm-input mt-1 w-full" value={form.tone_override || ""} onChange={(e) => set("tone_override", e.target.value || null)} data-testid="assistant-set-tone">
              <option value="">{t("Automatisch (geleerd: {x})", { x: profile?.tone === "brief" ? t("kort") : t("uitgebreid") })}</option>
              <option value="brief">{t("Kort & zakelijk")}</option><option value="detailed">{t("Uitgebreid")}</option>
            </select>
          </label>
        </div>
        <div>
          <div className="text-xs font-bold text-slate-600 mb-2">{t("Frequentie per signaaltype")}</div>
          <div className="space-y-1.5">
            {types.map((ty) => (
              <div key={ty} className="flex items-center justify-between text-sm" data-testid={`assistant-typefreq-${ty}`}>
                <span>{t(TYPE_LABEL_NL[ty] || ty)}</span>
                <select className="pm-input h-8 w-36" value={(form.type_frequency || {})[ty] || ""} onChange={(e) => setTypeFreq(ty, e.target.value)}>
                  <option value="">{t("Volgt algemeen")}</option><option value="daily">{t("Dagelijks")}</option><option value="weekly">{t("Wekelijks")}</option><option value="off">{t("Uit")}</option>
                </select>
              </div>
            ))}
          </div>
        </div>
        {err && <div className="text-xs text-red-600" data-testid="assistant-settings-error">{err}</div>}
        <button onClick={save} disabled={saving} className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="assistant-settings-save">
          {saving ? <Loader2 size={12} className="animate-spin" /> : ok ? <Check size={12} /> : <Save size={12} />} {ok ? t("Opgeslagen") : t("Instellingen opslaan")}
        </button>
      </div>

      <div className="pm-card p-5" data-testid="assistant-profile-panel">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center"><Brain size={16} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{t("Zelflerend")}</div>
            <div className="font-display font-black text-sm">{t("Wat de assistent van jou geleerd heeft")}</div>
          </div>
        </div>
        <div className="text-xs text-slate-500 mb-3">
          {t("Toon")}: <b>{profile?.tone === "brief" ? t("kort") : t("uitgebreid")}</b> ({profile?.tone_source === "learned" ? t("afgeleid uit je eigen berichten, gem. {n} tekens", { n: profile?.tone_avg_chars }) : t("standaard")}). {t("Gewicht < 0.45 → type verschuift automatisch naar wekelijks.")}
        </div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500"><th className="py-1">{t("Type")}</th><th>{t("Gewicht")}</th><th>{t("Drempel")}</th><th>✓</th><th>✕</th><th>zz</th><th>—</th></tr></thead>
          <tbody>
            {types.map((ty) => { const p = profile.types[ty]; return (
              <tr key={ty} className="border-t border-slate-100" data-testid={`assistant-profile-${ty}`}>
                <td className="py-1.5 font-semibold">{t(TYPE_LABEL_NL[ty] || ty)}</td>
                <td><div className="flex items-center gap-1.5"><div className="w-14 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-violet-500" style={{ width: `${Math.min(100, p.weight / 2 * 100)}%` }} /></div><span className="font-mono-tab">{p.weight.toFixed(2)}</span></div></td>
                <td className="font-mono-tab">{p.threshold_days}d</td>
                <td className="font-mono-tab text-emerald-700">{p.acted}</td>
                <td className="font-mono-tab text-red-600">{p.dismissed}</td>
                <td className="font-mono-tab text-slate-500">{p.snoozed}</td>
                <td className="font-mono-tab text-slate-400">{p.ignored}</td>
              </tr>
            ); })}
          </tbody>
        </table>
        <div className="text-[10px] text-slate-400 mt-2">{t("✓ actie ondernomen · ✕ afgewezen · zz gesnoozed · — genegeerd (3+ cycli). Alleen jouw eigen data, geen cross-account leren.")}</div>
      </div>
    </div>
  );
}
